import Dexie from 'dexie';
import { removeUndefinedValues } from '../util/util';

const db = new Dexie('gameVision');
db.version(1).stores({
    analyses: '&uid,userId',
    analysisData: '&uid,userId',
});
db.version(2).stores({
    users: '&userId'
});
db.version(3).stores({
    analyses: '&uid,userId',
    analysisData: '&uid,userId',
    users: '&userId',
    imageCache: '&url'
});
db.version(4).stores({
    pendingUploads: '&uid,userId,analysisUid'
});

export const getUserAnalysisList = async (userId, includeDeleted = false) => {
    const list = await db.analyses
        .where({ userId })
        .and(a => (includeDeleted || !a.deleted) && a.uid)
        .toArray();
    if (list.length === 0) return null;
    const result = {};
    list.forEach(a => {
        const { userId: _uid, ...rest } = a;
        result[a.uid] = rest;
    });
    return result;
};

export const createUserAnalysis = async (userId, analysis, analysisData) => {
    const uid = String(Date.now());
    await db.analyses.add({ ...analysis, uid, userId });
    await db.analysisData.add({ ...analysisData, uid, userId });
    return uid;
};

export const putUserAnalysis = async (userId, uid, analysis, analysisData) => {
    await db.analyses.put({ ...analysis, uid, userId });
    await db.analysisData.put({ ...analysisData, uid, userId });
    return uid;
};

export const getUserAnalysisData = async (userId, uid) => {
    const data = await db.analysisData.get(uid);
    if (data && data.userId !== userId) return null;
    return data || null;
};

export const deleteUserAnalysis = async (userId, uid) => {
    const item = await db.analyses.get(uid);
    if (item && item.userId !== userId) return null;
    if (item) {
        const now = new Date().toISOString();
        await db.analyses.update(uid, {
            deleted: true,
            deletedAt: now,
            lastUpdate: now,
        });
        return true;
    }
    return null;
};

export const updateAnalysisLastUpdate = async (userId, uid, lastUpdate) => {
    await db.analyses.update(uid, { lastUpdate });
    return true;
};

export const updateActions = async (userId, uid, side, groupIndex, actions) => {
    const data = await db.analysisData.get(uid);
    if (!data || data.userId !== userId) return false;
    if (!data[`${side}Actions`]) data[`${side}Actions`] = [];
    data[`${side}Actions`][groupIndex] = {
        ...(data[`${side}Actions`][groupIndex] || {}),
        actions
    };
    await db.analysisData.put(data);
    return true;
};

const setByPath = (obj, path, value) => {
    const parts = path.split('/');
    let current = obj;
    for (let i = 0; i < parts.length - 1; i++) {
        const part = parts[i];
        const next = parts[i + 1];
        const nextIsIndex = /^\d+$/.test(next);
        if (/^\d+$/.test(part)) {
            const idx = parseInt(part, 10);
            if (!Array.isArray(current)) current = current[part] = [];
            if (current[idx] === undefined) current[idx] = nextIsIndex ? [] : {};
            current = current[idx];
        } else {
            if (!current[part]) current[part] = nextIsIndex ? [] : {};
            current = current[part];
        }
    }
    const last = parts[parts.length - 1];
    if (/^\d+$/.test(last)) {
        const idx = parseInt(last, 10);
        if (!Array.isArray(current)) current[last] = [];
        current[idx] = value;
    } else {
        current[last] = value;
    }
};

export const setAnalysisActionData = async (userId, uid, side, groupIndex, actionIndex, dataArray) => {
    const data = await db.analysisData.get(uid);
    if (!data || data.userId !== userId) return false;
    const group = data[`${side}Actions`]?.[groupIndex];
    if (group?.actions?.[actionIndex]) {
        const cleanDataArray = removeUndefinedValues(dataArray);
        group.actions[actionIndex].data = cleanDataArray;
        await db.analysisData.put(data);
        return true;
    }
    return false;
};

export const updateMultipleNodes = async (userId, uid, analysisUpdates = {}, analysisDataUpdates = {}) => {
    await db.transaction('rw', db.analyses, db.analysisData, async () => {
        if (Object.keys(analysisUpdates).length) {
            const item = await db.analyses.get(uid);
            if (item && item.userId === userId) {
                Object.entries(analysisUpdates).forEach(([path, value]) => {
                    if (path.includes('/')) setByPath(item, path, value); else item[path] = value;
                });
                await db.analyses.put(item);
            }
        }
        if (Object.keys(analysisDataUpdates).length) {
            const item = await db.analysisData.get(uid);
            if (item && item.userId === userId) {
                Object.entries(analysisDataUpdates).forEach(([path, value]) => {
                    if (path.includes('/')) setByPath(item, path, value); else item[path] = value;
                });
                await db.analysisData.put(item);
            }
        }
    });
    return true;
};

export const saveTelestratorInfo = async (userId, uid, telestratorData, favoriteColors) => {
    await db.analysisData.update(uid, { telestratorData, favoriteColors });
    return true;
};

export const deleteTelestratorData = async (userId, uid, telestratorData) => {
    await db.analysisData.update(uid, { telestratorData });
    return true;
};

export const onConnectionChange = (callback) => {
    const handleOnline = () => {
        callback({ val: () => true });
    };

    const handleOffline = () => {
        callback({ val: () => false });
    };

    if (typeof window !== 'undefined') {
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        callback({ val: () => navigator.onLine });
    } else {
        callback({ val: () => true });
    }

    return () => {
        if (typeof window !== 'undefined') {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        }
    };
};

export const updateAnalysisData = async (userId, uid, updates) => {
    const data = await db.analysisData.get(uid);
    if (!data || data.userId !== userId) return;
    Object.entries(updates).forEach(([path, value]) => {
        if (path.includes('/')) setByPath(data, path, value); else data[path] = value;
    });
    await db.analysisData.put(data);
};

export const removeAnalysis = async (userId, uid) => {
    await db.analyses.delete(uid);
    await db.analysisData.delete(uid);
};

export const setUserPermission = async (userId, expirationDate) => {
    const user = await db.users.get(userId);
    if (user) {
        await db.users.update(userId, { expiration_date: expirationDate });
    } else {
        await db.users.add({ userId, expiration_date: expirationDate });
    }
    return true;
};

export const setUserBeta = async (userId, betaUser) => {
    const user = await db.users.get(userId);
    if (user) {
        await db.users.update(userId, { beta_user: betaUser });
    } else {
        await db.users.add({ userId, beta_user: betaUser });
    }
    return true;
};

export const getUserPermission = async (userId, setPermissionDate) => {
    const user = await db.users.get(userId);
    if (user && user.expiration_date !== undefined) {
        if (setPermissionDate) setPermissionDate(user.expiration_date);
        return user.expiration_date;
    }
    return null;
};

export const getUserBeta = async (userId, setUserBeta) => {
    const user = await db.users.get(userId);
    const beta = !!user?.beta_user;
    if (setUserBeta) setUserBeta(beta);
    return beta;
};

export const getUserWooviCustomer = async (userId) => {
    const user = await db.users.get(userId);
    return user?.wooviCustomer || null;
};

export const setUserWooviCustomer = async (userId, customer) => {
    const cleanCustomer = removeUndefinedValues(JSON.parse(JSON.stringify(customer)));
    const user = await db.users.get(userId);
    const updates = {
        wooviCustomer: cleanCustomer,
        wooviCustomerUpdatedAt: new Date().toISOString(),
    };

    if (user) {
        await db.users.update(userId, updates);
    } else {
        await db.users.add({ userId, ...updates });
    }

    return true;
};

export const setLastSyncDate = async (userId, lastSync) => {
    const user = await db.users.get(userId);
    if (user) {
        await db.users.update(userId, { last_sync: lastSync });
    } else {
        await db.users.add({ userId, last_sync: lastSync });
    }
    return true;
};

export const getLastSyncDate = async (userId) => {
    const user = await db.users.get(userId);
    return user?.last_sync || null;
};

export const getCachedImage = async (url) => {
    const item = await db.imageCache.get(url);
    return item ? item.blob : null;
};

export const setCachedImage = async (url, blob) => {
    await db.imageCache.put({ url, blob });
};

export const addPendingUpload = async (userId, analysisUid, folder, fileName, blob) => {
    const uid = `offline_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    await db.pendingUploads.put({ uid, userId, analysisUid, folder, fileName });
    await setCachedImage(uid, blob);
    return uid;
};

export const getPendingUploads = async (userId) => {
    return db.pendingUploads.where({ userId }).toArray();
};

export const removePendingUpload = async (uid) => {
    await db.pendingUploads.delete(uid);
};
